import { absoluteValue, createBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import type { Transfer } from '@0xtorch/core'
import { createSolanaAddressId } from '@pkg/solana'
import type { NativeTokenTransfer, SplTokenTransfer } from '@pkg/solana'
import { fromIsRelated, haveTokenTransfers, isFeePayer } from '../../conditions'
import { ADDRESSES, nativeAsset, nativeTokenDecimals } from '../../constants'
import { createAction, createTokenTransfer } from '../../generators'
import { createDirection } from '../../generators/createDirection'
import type { SolanaConverter } from '../../type'

/**
 * Orca Whirlpool の各種操作
 * - 流動性提供（OpenPositionWithTokenExtensions, IncreaseLiquidity）
 * - その他の操作
 *
 * **生成する Action**
 * - 流動性提供の場合: stake action
 *   - type = stake
 *   - transfers: 提供したトークンとLP NFT
 *
 * **条件 (一致すべき pattern)**
 * - from
 *   - relatedAddress 内の address である
 * - transfers
 *   - token transfer が存在する（必要に応じて）
 *
 * **判定理由**
 * - programId が whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc (Orca Whirlpool)
 * - ログメッセージから操作種別を判定
 */
const convertToActions: SolanaConverter = (args) => {
  const { transaction, source, relatedAddresses } = args

  // 送信者が関連アドレスかチェック
  if (!fromIsRelated(args)) {
    return undefined
  }

  // ログメッセージから操作種別を判定
  const logMessages = transaction.meta?.logMessages || []
  const isAddLiquidity = logMessages.some(
    (log: string) =>
      log.includes('OpenPositionWithTokenExtensions') ||
      log.includes('IncreaseLiquidity'),
  )

  // 流動性提供の場合
  if (isAddLiquidity && haveTokenTransfers(transaction)) {
    const actions: Action[] = []
    // add-liquidity action を生成
    const nativeTransfers: NativeTokenTransfer[] = []
    const splTransfers: SplTokenTransfer[] = []
    const wsolTransfers: SplTokenTransfer[] = []
    if (transaction.tokenTransfersFromInstructions) {
      for (const tokenTransfer of transaction.tokenTransfersFromInstructions) {
        if (tokenTransfer.type === 'native') {
          nativeTransfers.push(tokenTransfer)
        } else {
          splTransfers.push(tokenTransfer)
        }
      }
    }

    const liquidityTransfers = splTransfers.map((transfer) => {
      if (transfer.token.address === ADDRESSES.WSOL_MINT) {
        // あとでSOLの手数料を計算するために保存
        wsolTransfers.push(transfer)
      }
      return createTokenTransfer({
        tokenTransfer: transfer,
        relatedAddresses,
      })
    })

    const addLiquidityAction = createAction({
      type: 'add-liquidity',
      evidence: 'none',
      order: actions.length,
      transfers: liquidityTransfers.filter(Boolean) as Transfer[],
      transaction,
      source,
      comment: 'TODO: 流動性提供',
    })

    actions.push(addLiquidityAction)

    // native token transferの差額を計算(account毎)
    const nativeTransferAmount = nativeTransfers.reduce((acc, transfer) => {
      const direction = createDirection(
        transfer.from
          ? createSolanaAddressId({ address: transfer.from })
          : undefined,
        transfer.to
          ? createSolanaAddressId({ address: transfer.to })
          : undefined,
        relatedAddresses,
      )
      if (direction === 'none') {
        return acc
      }
      if (direction === 'out') {
        return acc - transfer.amount
      }
      return acc + transfer.amount
    }, BigInt(0))

    const wsolTransferAmount = wsolTransfers.reduce((acc, transfer) => {
      const direction = createDirection(
        transfer.from
          ? createSolanaAddressId({ address: transfer.from })
          : undefined,
        transfer.to
          ? createSolanaAddressId({ address: transfer.to })
          : undefined,
        relatedAddresses,
      )
      if (direction === 'none') {
        return acc
      }
      if (direction === 'out') {
        return acc - transfer.amount
      }
      return acc + transfer.amount
    }, BigInt(0))

    const feeAction = createAction({
      type: 'fee',
      evidence: 'none',
      order: actions.length,
      transfers: [
        {
          direction: 'out',
          asset: nativeAsset,
          amount: absoluteValue(
            createBigDecimal(
              nativeTransferAmount - wsolTransferAmount,
              nativeTokenDecimals,
            ),
          ),
        },
      ],
      transaction,
      source,
    })

    actions.push(feeAction)

    // トランザクション手数料をout transferとして追加
    if (isFeePayer(args) && transaction.meta?.fee) {
      const transactionFeeAction = createAction({
        order: actions.length,
        type: 'transaction-fee',
        transfers: [
          {
            direction: 'out',
            asset: nativeAsset,
            amount: createBigDecimal(
              BigInt(transaction.meta.fee),
              nativeTokenDecimals,
            ),
          },
        ],
        transaction,
        source,
      })
      actions.push(transactionFeeAction)
    }

    return actions
  }

  // その他の操作の場合は未実装
  return undefined
}

export default convertToActions
